import { LDFlagSet } from 'launchdarkly-js-sdk-common';
import { useLDClient, withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { useEffect } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';

import type { GetUserQueryGQLType } from '../../../types/dashboard-core-gqlTypes.generated.d';
import { ResourcesCalculationTerminologiesRoute } from './ResourcesCalculationTerminologiesRoute';
import { BorrowerTemplate } from 'borrower/domains/page/ui/BorrowerTemplate';
import { AssignmentRouter } from 'borrower/routes/assignment/Router';
import { BBPLMonitorRoute } from 'borrower/routes/BBPLMonitorRoute';
import { BorrowerHomeRoute } from 'borrower/routes/BorrowerHomeRoute';
import { BorrowerTasksRoute } from 'borrower/routes/BorrowerTasksRoute';
import { CreditAgreementsRoute } from 'borrower/routes/CreditAgreementRoute';
import { DeliverablesRouter } from 'borrower/routes/DeliverablesRouter';
import { EventsRoute } from 'borrower/routes/EventsRoute';
import { FundingRequestRouter } from 'borrower/routes/funding-requests/Router';
import { InsightsRouter } from 'borrower/routes/insights/Router';
import { LoanTapeUploaderRoute } from 'borrower/routes/LoanTapeUploaderRoute';
import { CreditFacilityReceivablesDashboardRouter } from 'borrower/routes/receivables/CreditFacilityReceivablesDashboardRouter';
import { ReceivableDetailsRoute } from 'borrower/routes/receivables/ReceivableDetailsRoute';
import { ReportsRoute } from 'borrower/routes/ReportsRoute';
import { SettingsRouter } from 'borrower/routes/settings/Router';
import { StatementsRouter } from 'borrower/routes/statements/Router';
import { StratsRoute } from 'borrower/routes/StratsRoute';
import { isQAUser } from 'packages/common-frontend/borrowerEnabledConfig';
import { heapIdentify } from 'packages/common-frontend/hooks/useHeap';
import { useOrganizationUiVisibility } from 'packages/common-frontend/hooks/useUiVisibility';

export const BorrowerRouter = withLDConsumer()(
  ({
    userData,
    flags,
  }: {
    userData: GetUserQueryGQLType;
    flags?: LDFlagSet;
  }) => {
    const ldClient = useLDClient();
    // @JonAtFinley @danitian replace with real UiVisibility returned from auth
    const {
      sideBarItems: sideBarItemsVisibility,
      defaultRoute,
    } = useOrganizationUiVisibility({
      mockUiVisibilityMode: flags?.isBorrowerLimitedOrganization
        ? 'borrowerLimited'
        : 'borrowerOwner',
      organizationSlug: userData.user?.organization.slug,
    });

    useEffect(() => {
      if (ldClient !== undefined && userData.user !== undefined) {
        ldClient.identify({
          kind: 'user',
          key: userData.user!.id,
          orgSlug: userData.user!.organization.slug,
          roleType: userData.user!.roleType,
          isInternalFinleyUser: isQAUser(userData.user!.email),
        });
      }
    }, [ldClient, userData]);

    if (!userData.user) {
      return <></>;
    }

    const { user } = userData;

    // identifies heap analytics info to logged in customer's email
    if (user && window.heap?.identify) {
      heapIdentify({ dashboardType: 'borrower', user });
    }

    const isManualUploaderOnly = flags?.enableManualUploaderOnly;
    if (isManualUploaderOnly) {
      return (
        <BorrowerTemplate>
          <Route path="/manual-uploader">
            <LoanTapeUploaderRoute />
          </Route>
          <Redirect to="/manual-uploader" />
        </BorrowerTemplate>
      );
    }

    // each route / subrouter below is wrapped in a route template with old dashboard formatting
    // as we work on updating overall dashboard UI, we can incrementally move each route out of the old template one at a time
    return (
      <BorrowerTemplate>
        <Switch>
          <Route exact path="/">
            {sideBarItemsVisibility.home ? (
              <BorrowerHomeRoute />
            ) : (
              <Redirect to={defaultRoute} />
            )}
          </Route>
          <Route path="/credit-agreements">
            <CreditAgreementsRoute />
          </Route>
          {flags?.showGraphMonitor && (
            <Route path="/bbpl-monitor">
              <BBPLMonitorRoute organizationId={user.organizationId} />
            </Route>
          )}
          <Route path="/deliverables">
            <DeliverablesRouter />
          </Route>
          <Route exact path="/events">
            <EventsRoute />
          </Route>
          <Route path="/funding-requests">
            <FundingRequestRouter userData={userData} />
          </Route>
          <Route exact path="/tasks">
            <BorrowerTasksRoute />
          </Route>
          {!flags?.hideManualUploader && (
            <Route path="/manual-uploader">
              <LoanTapeUploaderRoute />
            </Route>
          )}
          <Route path="/assignments">
            <AssignmentRouter />
          </Route>
          <Route path="/reports">
            <ReportsRoute />
          </Route>
          {flags?.showStratsPage && flags.showStratsPage !== 'hide' && (
            <Route path="/strats">
              <StratsRoute />
            </Route>
          )}
          <Route path="/insights">
            <InsightsRouter />
          </Route>
          <Route path="/calculation-terminologies">
            <ResourcesCalculationTerminologiesRoute />
          </Route>
          <Route path="/statements">
            <StatementsRouter />
          </Route>
          <Route path="/settings">
            <SettingsRouter />
          </Route>
          <Route path="/credit-facility-receivables">
            <CreditFacilityReceivablesDashboardRouter
              organizationId={user.organizationId}
            />
          </Route>
          <Route
            exact
            path="/receivables/:finleyReceivableId"
            component={ReceivableDetailsRoute}
          />
          <Redirect to="/" />
        </Switch>
      </BorrowerTemplate>
    );
  },
);
